import store from '@/store/store'
import { PREVIEW_ITEM, SECTION_FOCUSES } from '@/store/constants'
import { ACTION_TYPES, STATE_TYPES } from '@/utils/bot-json.constants'
import Vue from 'vue'
import previewUtils from '@/utils/preview.utils'
import toaster from '@/utils/toaster'
import stateTreeUtils from '@/utils/state-tree.utils'
import conversationModule from './conversation.module'
import is from 'ramda/src/is'

class ChatBubbleModule {

  actionId = {
    get() {
      return store.state.ui.preview.bubbleSelected.actionId
    }
  }

  bubbleSelected = {
    get() {
      return store.state.ui.preview.bubbleSelected
    },

    set(bubbleSelected) {
      store.commit('UI_BUBBLE_SELECTED', bubbleSelected)
    }
  }

  conditionSections = {
    get() {
      return store.state.ui.preview.conditionSections
    }
  }

  sectionFocus = {
    get() {
      return store.state.ui.preview.bubbleSelected.sectionFocus
    }
  }

  // TODO move stateTree to a separate module?
  stateTree = {
    get() {
      return store.state.ui.preview.stateTree
    }
  }

  /** @param focus accepts values of constants.js#SECTION_FOCUSES */
  focusSection(focus) {
    store.commit('UI_BUBBLE_SECTION_FOCUS', focus)
  }

  isAnswerEndFocused(stateId, value, isCondition) {
    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.type === PREVIEW_ITEM.ANSWER_END &&
           bubbleSelected.stateId === stateId &&
           bubbleSelected.value === value &&
           bubbleSelected.isCondition === isCondition
  }

  isWaitForInputFocused(stateId, nextStateId = null) {
    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.type === PREVIEW_ITEM.WAIT_FOR_INPUT &&
           bubbleSelected.stateId === stateId &&
           bubbleSelected.nextStateId === nextStateId
  }

  isRemoteBubbleFocused(stateId, actionId) {
    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.type === PREVIEW_ITEM.REMOTE_BUBBLE &&
           bubbleSelected.stateId === stateId &&
           bubbleSelected.actionId === actionId
  }

  isChatGPTBubbleFocused(stateId, actionId) {
    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.type === PREVIEW_ITEM.CHAT_GPT_BUBBLE &&
        bubbleSelected.stateId === stateId &&
        bubbleSelected.actionId === actionId
  }

  isConditionSectionFocused(stateId) {
    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.type === PREVIEW_ITEM.CONDITION_SECTION &&
           bubbleSelected.stateId === stateId
  }

  isCustomerBubbleFocused(isFirst) {
    if (!isFirst) return false

    const bubbleSelected = this.bubbleSelected.get()
    return bubbleSelected.isFirst
  }

  getIndexFromFocus(focus, focusType) {
    return previewUtils.getIndexFromFocus(focus, focusType)
  }

  isErrorBubbleFocused() {
    const error = store.state.ui.preview.error
    if (!is(Object, error)) return false

    const bubbleSelected = this.bubbleSelected.get()
    const { stateType, actionId, conditionCaseIndex, stateId, section, conditionStateId, nextStateId } = error

    switch (stateType) {
      case STATE_TYPES.OUTPUT:
        return bubbleSelected.actionId === actionId && bubbleSelected.sectionFocus === section
      case STATE_TYPES.REMOTE:
      case ACTION_TYPES.CHAT_GPT_GENERAL:
      case ACTION_TYPES.CHAT_GPT_CUSTOM:
        return bubbleSelected.actionId === actionId && bubbleSelected.stateId === stateId
      case STATE_TYPES.CONDITION:
        return bubbleSelected.stateId === conditionStateId && bubbleSelected.caseSelected === conditionCaseIndex
      case STATE_TYPES.SAVE_CONTEXT:
        return (bubbleSelected.stateId === stateId && bubbleSelected.nextStateId === nextStateId) ||
          (bubbleSelected.validationActionId === actionId && bubbleSelected.contextStateId === stateId)
      case ACTION_TYPES.VALIDATION:
        return bubbleSelected.validationActionId === actionId && bubbleSelected.contextStateId === stateId
      case 'none':
        return
      default:
        throw new Error('unknown state type: ' + stateType)
    }
  }

  highlightError(error, stateTree) {
    const {
      stateType, actionId, stateId, description, section, transitionStateId, nextStateId, conditionStateId, conditionCaseIndex, type, rejectedValue
    } = error.details

    if (error.code === 'validation-transition' || description.includes('Please add content')) {
      toaster.error(description)
    }
    switch (stateType) {
      case STATE_TYPES.OUTPUT:
        this.checkConditionCases(stateTree, stateId, actionId)
        this.selectBotBubble(actionId, stateId, section)
        if (error.code === 'validation-action-content' && type === 'text' && rejectedValue === null) {
          toaster.error('Message bubble cannot be empty')
        }
        return
      case STATE_TYPES.REMOTE:
        if ([ACTION_TYPES.CHAT_GPT_GENERAL, ACTION_TYPES.CHAT_GPT_CUSTOM].includes(type)) {
          this.selectChatGPTBubble(stateId, actionId)
        } else {
          this.selectRemoteBubble(stateId, actionId)
        }

        return
      case STATE_TYPES.CONDITION:
        this.selectConditionSection(conditionStateId, conditionCaseIndex)
        return
      case STATE_TYPES.SAVE_CONTEXT:
        this.selectWaitForInput(transitionStateId, nextStateId, actionId, stateId)
        if (error.code === 'validation-transition') toaster.error(description)
        if (!stateId) store.commit('UI_ANSWER_SET_ERROR', { ...error.details, stateId: transitionStateId })
        return
      case ACTION_TYPES.VALIDATION:
        this.selectWaitForInput(transitionStateId, nextStateId, actionId, stateId)
        return
      case 'none':
        toaster.error(description)
        return
      default:
        throw new Error('unknown state type: ' + stateType)
    }
  }

  checkConditionCases(stateTree, stateId, actionId) {
    const states = conversationModule.getRootJson().states
    for (const [conditionStateId, state] of Object.entries(states)) {
      if (state.type === STATE_TYPES.CONDITION) {
        const conditionNode = stateTreeUtils.findNode(stateTree, { stateId: conditionStateId, type: PREVIEW_ITEM.CONDITION_SECTION })
        for (let i = 0; i < conditionNode.cases.length; i++) {
          const nodeFound = stateTreeUtils.findNode(conditionNode.cases[i].node, { stateId, actionId, type: PREVIEW_ITEM.BOT_BUBBLE })
          if (nodeFound) {
            this.selectConditionSection(conditionStateId, i)
            return
          }
        }
      }
    }
  }

  selectBubbleByActionType(actionType, stateId, actionId) {
    switch(actionType) {
      case ACTION_TYPES.REMOTE:
        return this.selectRemoteBubble(stateId, actionId, true)
      case ACTION_TYPES.CHAT_GPT_GENERAL:
      case ACTION_TYPES.CHAT_GPT_CUSTOM:
        return this.selectChatGPTBubble(stateId, actionId, true)
      default:
        return this.selectBotBubble(actionId, stateId, SECTION_FOCUSES.NONE, true)
    }
  }

  /** If no 'actionId' and 'stateId' is set no bubble will be selected. */
  selectBotBubble(actionId = null, stateId = null, sectionFocus = SECTION_FOCUSES.NONE, isTemplate = false, inNestedCondition = false) {
    this.bubbleSelected.set({
      actionId, stateId, isTemplate, inNestedCondition
    })

    Vue.nextTick(() => {
      this.focusSection(sectionFocus)
    })
  }

  selectCustomerBubble(text, isFirst) {
    store.commit('UI_CUSTOMER_BUBBLE_SELECTED', { text, isFirst })
  }

  selectWaitForInput(stateId, nextStateId, validationActionId = null, contextStateId = null, isTemplate = false, inNestedCondition = false) {
    store.commit('UI_WAIT_FOR_INPUT_SELECTED', {
      stateId, nextStateId, validationActionId, contextStateId, isTemplate, inNestedCondition
    })
  }

  selectRemoteBubble(stateId, actionId, isTemplate = false, inNestedCondition = false) {
    store.commit('UI_REMOTE_BUBBLE_SELECTED', {
      stateId, actionId, isTemplate, inNestedCondition
    })
  }

  selectChatGPTBubble(stateId, actionId, isTemplate = false, inNestedCondition = false) {
    store.commit('UI_CHAT_GPT_BUBBLE_SELECTED', {
      stateId, actionId, isTemplate, inNestedCondition
    })
  }

  selectAnswerEnd(stateId, value, isTemplate, isCondition, conditionEndStateId, isPostback, isQuickReply, inNestedCondition = false, answerId = null) {
    store.commit('UI_ANSWER_END_SELECTED', {
      stateId, value, isTemplate, isCondition, conditionEndStateId, isPostback, isQuickReply, inNestedCondition, answerId
    })
  }

  selectConditionSection(stateId, caseSelectedIndex = 0, isTemplate = false) {
    store.commit('UI_CONDITION_SELECTED', {
      stateId, caseSelected: caseSelectedIndex, isTemplate
    })
    store.commit('UI_PREVIEW_CONDITION_SECTION_PUT', {
      stateId, caseSelected: caseSelectedIndex
    })
  }

  clearConditionSections() {
    store.commit('UI_PREVIEW_CONDITION_SECTION_CLEAR')
  }

  emptyAnswerEnd = {
    get() {
      return store.state.ui.preview.emptyAnswerEnd
    },

    set(emptyAnswerEnd) {
      store.commit('UI_PREVIEW_EMPTY_ANSWER_END_SET', emptyAnswerEnd)
    }
  }

  getConditionCaseSelected(conditionStateId) {
    return this.conditionSections.get()[conditionStateId] || 0
  }

  setStateTree(stateTree) {
    store.commit('UI_PREVIEW_STATE_TREE_SET', stateTree)
  }

  collapseKey = {
    get() {
      return store.state.ui.preview.collapseKey
    },
    set(collapseKey) {
      store.commit('UI_PREVIEW_COLLAPSE_KEY_SET', collapseKey)
    }
  }

}

export default new ChatBubbleModule()
